import AppRoutes from "@/constants/routes/AppRoutes";
import { DocumentTab } from "@/components/content-sidebar/document-sidebar/document-tab-triggers";
import { Domain, getDomain } from "@/utils/domain";

/**
 * View context determines the navigation context (chat vs legislation vs workspace)
 */
export type ViewContext = "chat" | "legislation" | "workspace";

/**
 * Breadcrumb configuration for a view
 */
export interface BreadcrumbConfig {
  title: string;
  fullTitle?: string;
  url: string;
  onClick?: () => void;
  kind?: "link" | "ellipsis";
  hiddenParents?: BreadcrumbConfig[];
  widthTier?: "anchor" | "document";
}

/**
 * Configuration for a legislation view
 */
export interface LegislationViewConfig {
  context: ViewContext;
  getBreadcrumbs: (params: {
    datasource?: string;
    onDatasourceClick?: () => void;
  }) => BreadcrumbConfig | BreadcrumbConfig[];
  sidebarTabs: DocumentTab[];
  /**
   * Whether to conditionally show sidebar based on document references
   * If true, sidebar only shows when documentReferences.length > 0
   */
  conditionalSidebar: boolean;
  /**
   * Default page title when legislation data is not yet loaded
   */
  defaultTitle: string;
}

/**
 * Get the chat parent title based on the current domain
 */
const getChatParentTitle = (): string => {
  const domain = getDomain(window.location.hostname);
  return domain === Domain.LODA ? "Loda AI" : "Lara AI";
};

/**
 * Configuration for the chat HTML view (accessed from conversation references)
 */
export const CHAT_HTML_VIEW_CONFIG: LegislationViewConfig = {
  context: "chat",
  getBreadcrumbs: (): BreadcrumbConfig => ({
    title: getChatParentTitle(),
    url: AppRoutes.chat,
  }),
  sidebarTabs: [
    DocumentTab.TABLE_OF_CONTENTS,
    DocumentTab.DOCUMENT_METADATA,
    DocumentTab.CHAT_REFERENCES,
    DocumentTab.DOCUMENT_REFERENCES,
  ],
  conditionalSidebar: true,
  defaultTitle: "Kildevisning",
};

/**
 * Configuration for the legislation HTML view (accessed from legislation browse)
 */
export const LEGISLATION_HTML_VIEW_CONFIG: LegislationViewConfig = {
  context: "legislation",
  getBreadcrumbs: ({ datasource, onDatasourceClick }): BreadcrumbConfig[] => [
    {
      title: "Kilder",
      url: AppRoutes.legislations,
    },
    {
      title: datasource || "",
      url: AppRoutes.legislations,
      onClick: onDatasourceClick,
    },
  ],
  sidebarTabs: [
    DocumentTab.TABLE_OF_CONTENTS,
    DocumentTab.DOCUMENT_METADATA,
    DocumentTab.DOCUMENT_REFERENCES,
  ],
  conditionalSidebar: false,
  defaultTitle: "Lovgivning",
};

/**
 * Configuration for the chat PDF view (accessed from conversation references)
 */
export const CHAT_PDF_VIEW_CONFIG: LegislationViewConfig = {
  context: "chat",
  getBreadcrumbs: (): BreadcrumbConfig => ({
    title: getChatParentTitle(),
    url: AppRoutes.chat,
  }),
  sidebarTabs: [
    DocumentTab.DOCUMENT_METADATA,
    DocumentTab.CHAT_REFERENCES,
    DocumentTab.DOCUMENT_REFERENCES,
  ],
  conditionalSidebar: true,
  defaultTitle: "Kildevisning",
};

/**
 * Configuration for the legislation PDF view (accessed from legislation browse)
 */
export const LEGISLATION_PDF_VIEW_CONFIG: LegislationViewConfig = {
  context: "legislation",
  getBreadcrumbs: (): BreadcrumbConfig => ({
    title: "Kilder",
    url: AppRoutes.legislations,
  }),
  sidebarTabs: [],
  conditionalSidebar: false,
  defaultTitle: "Kildevisning",
};

/**
 * Configuration for workspace document views (accessed from workspace)
 */
export const WORKSPACE_DOCUMENT_VIEW_CONFIG: LegislationViewConfig = {
  context: "workspace",
  getBreadcrumbs: ({ datasource }): BreadcrumbConfig => ({
    title: datasource || "Workspace",
    url: AppRoutes.workspaces,
  }),
  sidebarTabs: [
    DocumentTab.DOCUMENT_METADATA,
    DocumentTab.DOCUMENT_REFERENCES,
    DocumentTab.VERSION_HISTORY,
  ],
  conditionalSidebar: false,
  defaultTitle: "Dokumentvisning",
};

/**
 * Configuration for workspace document view from chat (accessed from chat references)
 */
export const CHAT_WORKSPACE_DOCUMENT_VIEW_CONFIG: LegislationViewConfig = {
  context: "chat",
  getBreadcrumbs: (): BreadcrumbConfig => ({
    title: getChatParentTitle(),
    url: AppRoutes.chat,
  }),
  sidebarTabs: [
    DocumentTab.DOCUMENT_METADATA,
    DocumentTab.CHAT_REFERENCES,
    DocumentTab.DOCUMENT_REFERENCES,
    DocumentTab.VERSION_HISTORY,
  ],
  conditionalSidebar: false,
  defaultTitle: "Dokumentvisning",
};

/**
 * Determine which config to use based on the current route path
 */
export const getViewConfigFromPath = (
  pathname: string,
  viewType: "html" | "pdf"
): LegislationViewConfig => {
  const isChatContext = pathname.startsWith("/chat");
  const isWorkspaceContext =
    pathname.includes("/workspace/") || pathname.includes("/workspaces/");

  if (isWorkspaceContext) {
    return isChatContext
      ? CHAT_WORKSPACE_DOCUMENT_VIEW_CONFIG
      : WORKSPACE_DOCUMENT_VIEW_CONFIG;
  }

  if (viewType === "html") {
    return isChatContext ? CHAT_HTML_VIEW_CONFIG : LEGISLATION_HTML_VIEW_CONFIG;
  }

  return isChatContext ? CHAT_PDF_VIEW_CONFIG : LEGISLATION_PDF_VIEW_CONFIG;
};
